import { AppRoutes } from '@/pages/routes-config';

const SUPPORT_BASE = import.meta.env.VITE_SUPPORT_BASE_URL || 'https://support.proposal.biz';
const MARKETING_BASE = import.meta.env.VITE_PUBLIC_MARKETING_SITE_URL || 'https://www.proposal.biz';

// Links that live inside the app must opt out of the marketing site's signed-in redirect
// (FEAT-051), otherwise clicking them bounces the user straight back into the app. The
// param is consumed and removed from the address bar on arrival.
const marketingOptOut = (path: string) =>
  `${MARKETING_BASE}${path}${path.includes('?') ? '&' : '?'}noRedirect=true`;

export const Config = {
  DELAYED_OPERATION_NOTICE_MS: 5000,
  // Fillable text textarea field: visible lines in the field and its settings range.
  INLINE_TEXTAREA: {
    DEFAULT_ROWS: 3,
    MIN_ROWS: 2,
    MAX_ROWS: 10,
  },
  DOCUMENT: {
    THEME_VARIABLES: {
      TEMPLATE_CONTAINER_WIDTH: '700px',
    },
  },
  PRESENTATION: {
    THEME_VARIABLES: {
      TEMPLATE_CONTAINER_WIDTH: '1280px',
    },
  },
  PRESENT_MODE: {
    EDGE_NOTICE_MS: 1800,
    EDGE_BUMP_PX: 14,
    EDGE_BUMP_MS: 420,
    EDGE_DRAG_MIN_PX: 24,
  },
  // WCAG AA for body text. Smart block text below this on its surface is recolored.
  SMART_BLOCK_MIN_TEXT_CONTRAST: 4.5,
  WEB_PAGE: {
    THEME_VARIABLES: {
      TEMPLATE_CONTAINER_WIDTH: '1280px',
    },
  },
  // Template preview zoom base, and the viewport width the marketing site captures a
  // template snapshot at (the preview's zoom is 1 there).
  TEMPLATE_DESIGN_WIDTH: {
    document: 980,
    presentation: 1130,
    webpage: 1280,
  },
  // Contract with the marketing site, which renders snapshots inside this wrapper class
  // (pbz-marketing src/layouts/components/TemplateSnapshot.astro).
  TEMPLATE_SNAPSHOT: {
    WRAPPER_CLASS: 'pbz-snapshot',
    FONT_FAMILY_PREFIX: 'pbz ',
    HEADING_DEMOTION_LEVELS: 2,
    // Root font size that rem and em widths in media queries resolve against.
    ROOT_FONT_SIZE_PX: 16,
  },
  PROJECT_AGENT: {
    REASONING_TITLE_MAX_LENGTH: 90,
    REASONING_ELLIPSIS_STEP_MS: 380,
    // Changed pages previewed per version in the Original / Modified card.
    COMPARISON_PREVIEW_PAGE_LIMIT: 2,
  },
  DOCUMENT_AGENT: {
    // Not `google-font-`: the builder's font manager removes every link with that prefix.
    THEME_PREVIEW_FONT_LINK_PREFIX: 'theme-preview-font-',
    // A stream that sends nothing new for this long shows the thinking dots again.
    STREAM_IDLE_LOADER_DELAY_MS: 1200,
    // The thinking loader starts on the first word, then swaps to a random other one this often.
    THINKING_WORD_INTERVAL_MS: 2000,
    THINKING_WORDS: [
      'Thinking',
      'Pondering',
      'Mulling it over',
      'Brainstorming',
      'Sketching ideas',
      'Connecting the dots',
      'Drafting',
      'Weighing options',
      'Shaping the plan',
      'Polishing',
    ],
    // Brand lookup for the first message. Past this the chat starts without a brand theme.
    BRAND_SEARCH_TIMEOUT_MS: 4000,
    // Skipped brand cards remembered in localStorage, newest kept.
    BRAND_SEARCH_SKIPPED_CHATS_KEPT: 50,
  },
  WEB_RESEARCH: {
    // Sources listed before "Show more" when the research step is expanded.
    VISIBLE_SOURCE_LIMIT: 5,
    // Favicons stacked in the collapsed summary row.
    FAVICON_STACK_LIMIT: 4,
    FAVICON_URL: 'https://www.google.com/s2/favicons',
    FAVICON_FETCH_SIZE_PX: 32,
  },
  CHART_BUILDER: {
    FALLBACK_WIDTH_PX: 600,
    FALLBACK_HEIGHT_PX: 300,
    TITLE_MAX_LINES: 2,
    // Width of an average glyph as a share of the font size; Plotly cannot measure text before it draws.
    TEXT_CHAR_WIDTH_RATIO: 0.55,
    TITLE_LINE_HEIGHT_RATIO: 1.25,
    // Charts narrower than this get a smaller title and two legend entries per row.
    COMPACT_WIDTH_PX: 440,
    COMPACT_TITLE_MAX_FONT_SIZE: 14,
    // Space between the top edge and the title, and between title, legend and plot.
    HEADER_EDGE_PADDING_PX: 8,
    HEADER_GAP_PX: 8,
    TITLE_SIDE_PADDING_PX: 16,
    LEGEND_ROW_PADDING_PX: 8,
    // Width of the colour swatch plus its gap in a side legend entry.
    LEGEND_SWATCH_WIDTH_PX: 36,
    // A side legend never takes more than this share of the chart width.
    LEGEND_SIDE_MAX_WIDTH_RATIO: 0.3,
    LEGEND_ENTRY_WIDTH_RATIO: 0.28,
    LEGEND_ENTRY_WIDTH_RATIO_COMPACT: 0.48,
    AXIS_LABEL_MAX_LENGTH: 25,
    LABEL_INPUT_MAX_LENGTH: 30,
    TITLE_INPUT_MAX_LENGTH: 60,
    FALLBACK_PRIMARY_COLOR: '#6366f1',
    FALLBACK_TEXT_COLOR: '#0f172a',
    FALLBACK_BACKGROUND_COLOR: '#ffffff',
    FALLBACK_FONT_FAMILY: 'Inter, sans-serif',
    HOVERLABEL_TEXT_COLOR: '#ffffff',
    GRID: {
      dark: {
        grid: 'rgba(255, 255, 255, 0.10)',
        zeroLine: 'rgba(255, 255, 255, 0.18)',
        axis: 'rgba(255, 255, 255, 0.22)',
        hoverBg: '#1e293b',
      },
      light: {
        grid: 'rgba(15, 23, 42, 0.08)',
        zeroLine: 'rgba(15, 23, 42, 0.14)',
        axis: 'rgba(15, 23, 42, 0.20)',
        hoverBg: '#0f172a',
      },
    },
  },
  // Background-layout overlay used when the page has none saved; colour follows the card colour
  IMAGE_BACKGROUND_OVERLAY: {
    overlayEffect: 'faded' as const,
    overlayIntensity: 30,
  },
  IMAGE_GENERATION: {
    REQUEST_POLL_INTERVAL_MS: 4000,
  },
  STREAM_BUILDER: {
    PREVIEW_PARSE_INTERVAL_MS: 1000,
  },
  // Flag-gates the durable chat-to-proposal workflow (POC). When true, the client
  // POSTs to the durable start route instead of the legacy route. Mirrors
  // NEXT_PUBLIC_DURABLE_CHAT_TO_PROPOSAL in apps/next. Defaults to false so the
  // legacy route stays the default.
  DURABLE_CHAT_TO_PROPOSAL: import.meta.env.VITE_DURABLE_CHAT_TO_PROPOSAL === 'true',
  // Flag-gates the durable uploaded-document / URL import workflow (FEAT-046). Kept
  // separate from the chat flag: different routes and rollout risk, so one can ship
  // without the other. Mirrors NEXT_PUBLIC_DURABLE_IMPORT_GENERATION in apps/next.
  DURABLE_IMPORT_GENERATION: import.meta.env.VITE_DURABLE_IMPORT_GENERATION === 'true',
  VARIABLE_SEARCH_CHARACTER: '[',
  NEST_URL: import.meta.env.VITE_NEST_API_URL,
  PYTHON_URL: import.meta.env.VITE_PYTHON_API_URL,
  NEXT_URL: import.meta.env.VITE_NEXT_API_URL,
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  IS_PRODUCTION_DEPLOYMENT: import.meta.env.VITE_DEPLOYED_ENV === 'production',
  IS_STAGING_DEPLOYMENT: import.meta.env.VITE_DEPLOYED_ENV === 'staging',
  SENTRY: {
    // Max-update-depth / recursive trees produce megabyte stacks that Sentry drops.
    COMPONENT_STACK_MAX_LENGTH: 4000,
    ERROR_STACK_MAX_FRAMES: 40,
    // Hosts whose fetch/XHR/resource spans are dropped so document-load waterfalls
    // only contain first-party work (Nest, Next, own assets, document images).
    TRACE_IGNORE_HOSTS: [
      'google-analytics.com',
      'googletagmanager.com',
      'googleadservices.com',
      'google.com',
      'doubleclick.net',
      'optimonk.com',
      'neetoauth.com',
      'ipgeolocation.io',
      'posthog.com',
      'clarity.ms',
      'sentry.io',
      'facebook.com',
      'facebook.net',
      'linkedin.com',
    ],
  },
  ADMIN_ORG: Number(import.meta.env.VITE_ADMIN_ORG) || 0,
  VITE_AUTH_V2_ENABLED: import.meta.env.VITE_AUTH_V2_ENABLED === 'true',
  HELP_LAUNCHER_ENABLED: import.meta.env.VITE_HELP_LAUNCHER_ENABLED === 'true',
  // Default right sidebar view on desktop: 'pages' (default) or 'agent'
  DEFAULT_RIGHT_SIDEBAR_VIEW:
    import.meta.env.VITE_DEFAULT_RIGHT_SIDEBAR_VIEW === 'agent' ? 'agent' : 'pages',
  // Mirrors apps/next's NEXT_PUBLIC_ONBOARDING_FLOW_ENABLED (default false). Free-plan orgs stay
  // billingState 'pending' forever, so gating the onboarding-wizard redirect behind this flag is
  // what lets them reach the document agent chat without ever paying.
  ONBOARDING_FLOW_ENABLED: import.meta.env.VITE_ONBOARDING_FLOW_ENABLED === 'true',
  RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  // Public Brandfetch client ID. Brand search must run in the user's browser: Brandfetch rate
  // limits per IP (200 requests / 5 min), so a server proxy would share one limit across users.
  BRANDFETCH_CLIENT_ID: import.meta.env.VITE_BRANDFETCH_CLIENT_ID || '',
  // Cutover date (ISO string) for trigger-based feedback prompts (AI-generation rating,
  // document-sharing rating). Only proposals generated after this date are eligible.
  FEEDBACK_ENABLED_FROM: import.meta.env.VITE_FEEDBACK_ENABLED_FROM,
  // Same OAuth client as apps/next's NEXT_PUBLIC_GOOGLE_CLIENT_ID — the guest app's One Tap prompt
  // authenticates against the same better-auth server (Config.NEXT_URL).
  GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
  // Origin of the authed React builder (where /project/agent lives). Separate from the guest app in
  // prod; used to divert a logged-in guest into the real authed chat. Unset in the single-Vite dev
  // setup, where the guest gate falls back to the dashboard create screen.
  REACT_DASHBOARD_URL: import.meta.env.VITE_REACT_DASHBOARD_URL,
  LARAVEL_URL: import.meta.env.VITE_LARAVEL_URL,
  INTEGRATION_REDIRECT_URL: import.meta.env.VITE_INTEGRATION_REDIRECT_URL || '',
  DASHBOARD_URL: import.meta.env.VITE_DASHBOARD_API_URL,
  BUILDER_DOMAIN: import.meta.env.VITE_BUILDER_DOMAIN,
  CLARITY_ID: import.meta.env.VITE_CLARITY_ID,
  PUBLIC_MARKETING_SITE_URL: MARKETING_BASE,
  APP_RUN_TYPE: import.meta.env.VITE_APP_RUN_TYPE,
  MOBILE_BUILDER_RESTRICTION_SESSION_KEY: 'mobile-builder-restriction-bypassed',
  // Phones (below the Tailwind `sm` breakpoint) get the AI agent as a bottom dock + drawer.
  PHONE_MAX_WIDTH: 639,
  // Height reserved at the bottom of the canvas so the collapsed agent dock never covers content.
  MOBILE_AGENT_DOCK_HEIGHT: 72,
  // One-tap emoji reactions at the top of the phone comment sheet.
  QUICK_REACTION_EMOJIS: ['👍', '❤️', '🎉', '👀', '🔥'],
  // Gap (px) between selected text and the selection toolbar on phones, which sits below the text
  // so the system's cut/copy menu above it stays clear. Larger than desktop to clear the drag handles.
  MOBILE_SELECTION_TOOLBAR_GAP: 28,
  // The agent drawer is custom (it stays mounted while closed); other phone sheets use Modal.
  MOBILE_AGENT_DRAWER: {
    // Space left above the drawer so the builder header stays visible.
    TOP_GAP: 56,
    // Swipe distance (px) or velocity (px/s) on the handle that closes the drawer.
    CLOSE_DRAG_DISTANCE: 80,
    CLOSE_DRAG_VELOCITY: 500,
    // Screen covered (px) before it counts as the keyboard, not the browser's toolbar changing.
    KEYBOARD_MIN_INSET: 150,
    ANIMATION_MS: 300,
    // Ease-out-expo, the entrance curve from DESIGN.md.
    EASE: [0.16, 1, 0.3, 1] as [number, number, number, number],
  },
  // Fixed phone heights (fraction of the visible screen) for Modal's `mobileHeight`.
  MOBILE_MODAL_HEIGHT: {
    // Leaves part of the page visible above the insert sheet.
    INSERT_SHEET: 0.7,
    // Leaves the builder header visible above the menu.
    HEADER_MENU: 0.92,
    // Right-sidebar panels (Pages, Recipients, Comments, ...) and block sheets (image, icon, ...).
    PANEL: 0.92,
  },
  // Brand panel shown once per workspace, on its first AI or template document (FEAT-083).
  FIRST_DOCUMENT_BRAND_SETUP: {
    SEARCH_URL: 'https://api.brandfetch.io/v2/search',
    SEARCH_DEBOUNCE_MS: 300,
    SEARCH_MIN_CHARS: 2,
    // Brandfetch rejects a `limit` above 5, so the list is trimmed client-side instead.
    SEARCH_RESULT_LIMIT: 5,
    ASSET_MAX_BYTES: 2 * 1024 * 1024,
    LOGO_TYPES: ['image/png', 'image/webp', 'image/jpeg', 'image/svg+xml'],
    FAVICON_TYPES: ['image/png', 'image/webp', 'image/x-icon', 'image/vnd.microsoft.icon'],
    // The panel's theme editor saves this long after the last change.
    THEME_AUTO_SAVE_DEBOUNCE_MS: 800,
  },
  // Guests get no persisted chat history — cap ephemeral turns to bound AI spend before auto-handoff.
  GUEST_CHAT_MAX_TURNS: Number(import.meta.env.VITE_GUEST_CHAT_MAX_TURNS) || 20,
  // Guest AI generation is capped at this many pages. The client aborts the stream once the model
  // emits more, keeps the valid pages already parsed, and surfaces a signup nudge. Manual page
  // adds after generation are uncapped. Mirrors GUEST_MAX_GENERATED_PAGES in apps/next constants.
  GUEST_MAX_GENERATED_PAGES: 15,
  GUEST_SIGNUP: {
    OTP_LENGTH: 6,
    SUCCESS_REDIRECT_DELAY_MS: 400,
    TOUR_HANDOFF_MAX_AGE_SECONDS: 30 * 24 * 60 * 60,
  },
  COLLAB_URL: import.meta.env.VITE_WS_API_URL
    ? import.meta.env.VITE_WS_API_URL?.replace('http', 'ws').replace('https', 'ws')
    : 'ws://localhost:3003',
  // PartyKit configuration (same domain deployment)
  PARTYKIT_HOST: import.meta.env.VITE_PARTYKIT_HOST || 'localhost:1999',
  // How long the builder waits before surfacing a transport warning. This does not stop the
  // provider's retry loop. Initial failure renders the REST document as a read-only fallback.
  COLLABORATION_SYNC_TIMEOUT_MS: 20000,
  // Close code the room sends when a recipient signs (FEAT-085). Matches apps/partykit config.
  COLLABORATION_DOCUMENT_LOCKED_CLOSE_CODE: 4001,
  // Code nest-api puts on a 403 for a signed document. Matches SIGNED_DOCUMENT_ERROR_CODE there.
  SIGNED_DOCUMENT_ERROR_CODE: 'DOCUMENT_SIGNED',
  // Right-rail panels hidden once a document is signed: they change the document, or comment on it.
  SIGNED_LOCKED_RIGHT_PANELS: [
    'agent',
    'evaluate',
    'design',
    'page_settings',
    'comments',
  ] as readonly string[],
  // Once the room is degraded (that timeout, or the browser going offline) the saved pages are
  // fetched instead. A failed fetch is retried after each delay, under the splash; after the last
  // one the editor shows a load error with a retry button.
  COLLABORATION_SAVED_PAGES_RETRY_DELAYS_MS: [5000, 10000, 20000],
  // A sender field write for a page the room has not persisted yet is rejected, so it is flushed
  // and sent again after each delay. A user save is short: the signature modal cannot close while
  // it runs. The background auto-import covers the room's 30s writeback ceiling.
  SENDER_FIELD_WRITE_RETRY_DELAYS_MS: [0, 2000],
  SENDER_FIELD_AUTO_IMPORT_RETRY_DELAYS_MS: [0, 2000, 8000, 20000],
  // A durable generation still marked running after this long died without recording its
  // outcome, so Retry starts a new run instead of reading it. Matches the Next.js start route.
  DURABLE_GENERATION_STALE_AFTER_MS: 15 * 60 * 1000,
  // A dropped generation stream is read again after each of these delays, once the tab is
  // visible and online. The same delays space out a start request that got no response.
  DURABLE_STREAM_RECONNECT_DELAYS_MS: [2000, 5000, 10000],
  // How long a reconnect waits for the browser to report a network before trying anyway.
  DURABLE_STREAM_ONLINE_WAIT_MS: 30 * 1000,
  // A start request that got no response is sent again only within this long of the first
  // send. Later, the run it started could have finished, so Retry handles it instead.
  DURABLE_STREAM_START_RESEND_WINDOW_MS: 30 * 1000,
  USE_PARTYKIT: import.meta.env.VITE_USE_PARTYKIT === 'true',
  PUBLIC_ROUTES: [AppRoutes.view(''), AppRoutes.share(''), AppRoutes.export()],
  SUPPORT_URLS: {
    BASE: SUPPORT_BASE,
    BUILDER_LEFT_PANEL: `${SUPPORT_BASE}/articles/builder-2`,
  },
  EXTERNAL_URLS: {
    BOOK_A_CALL: 'https://proposal-biz.neetocal.com/proposalbiz',
  },
  MARKETING_URLS: {
    TERMS_AND_CONDITIONS: marketingOptOut('/term-and-condition/'),
    PRIVACY_POLICY: marketingOptOut('/privacy-policy/'),
    TEMPLATES: marketingOptOut('/templates/'),
  },
  ENHANCE_PROMPT_PURPOSES: {
    IMAGE_GENERATION: 'img',
    GENERATE_WITH_AI: 'gen_with_ai_prompt_enh',
    ADD_SECTION_WITH_AI: 'add_section_prompt_enh',
  },
  REPHRASE_PURPOSES: {
    fix_grammar: 'Fix Grammar',
    make_it_large: 'Make it Large',
    make_it_small: 'Make it Small',
    rephrase: 'Rephrase',
  },
  PERMISSION_LEVELS: {
    VIEW: 'view',
    COMMENT: 'comment',
    EDIT: 'edit',
    OWNER: 'owner',
  },
  HEARTBEAT_INTERVAL: 30 * 1000,
  DEBOUNCE_TIME: 1000,
  BLOCK_UPDATE_DEBOUNCE_MS: 500,
  SENDER_FIELD_TEXT_SAVE_DEBOUNCE_MS: 400,
  LIVE_PUBLISH_HASH_DEBOUNCE_MS: 1000,
  SHEET_CLOSE_ANIMATION_MS: 220,
  COLLABORATION_TEXT_COMMIT_DEBOUNCE_MS: 1500,
  PAGE_BOUNDARY_IDLE_DEBOUNCE_MS: 500,
  MOBILE_BREAKPOINT_PX: 640,
  EMOJI_PICKER_NARROW_BREAKPOINT_PX: 350,
  THUMBNAIL_PREVIEW_ROOT_MARGIN: '80px 0px',
  THUMBNAIL_ACTIVE_PAGE_FOLLOW_DELAY_MS: 120,
  GUEST_AUTOSAVE_DEBOUNCE_MS: 2500,
  // Automatic reloads after a lazy chunk fails to load. One reload fixes a stale deploy; repeats
  // mean a chunk that stays unreachable (blocked, offline), and each reload risks unsaved edits.
  PRELOAD_RELOAD: {
    COOLDOWN_MS: 60_000,
    WINDOW_MS: 600_000,
    MAX_IN_WINDOW: 3,
  },
  COMMENTS: {
    MAX_LENGTH: 5000,
    COUNTER_WARN_RATIO: 0.9,
  },
  SHARE_EMAIL: {
    MESSAGE_MAX_LENGTH: 500,
  },
  FOLDERS: {
    // Mirrors MAX_FOLDER_DEPTH in apps/nest-api/src/common/constants.ts.
    MAX_DEPTH: 5,
    NAME_MAX_LENGTH: 255,
    TREE_INDENT_PX: 20,
  },
  SPLASH_HIDE_DELAY_MS: 100,
  LOAD_TELEMETRY: {
    // A document that never paints a page (empty draft, agent generation, abandoned tab)
    // still has to report, or the slowest opens are the ones missing from the data. Kept above
    // COLLABORATION_SYNC_TIMEOUT_MS so a load that falls back to REST can still paint in time.
    TIMEOUT_MS: 30000,
    // Chrome fetches that start as splash hides (comments, credits, live-publish, recipients)
    // should still land on the document.load span after first paint.
    SPAN_IDLE_MS: 2000,
  },
  DEFAULT_USER_PROMPT: 'Keep it long professional',
  SESSION_STATUS: {
    Draft: 'Draft',
    Sent: 'Sent',
    Expired: 'Expired',
    Finished: 'Finished',
  },
  MAX_LIMIT_FOR_UNDO_REDO: 50,
  HISTORY: {
    MAX_RETAINED_BYTES: 64 * 1024 * 1024,
  },
  // Auto-derived from first heading when page is "Untitled Page"; must stay under
  // proposal_blocks.name varchar(255). Keep short for sidebar/nav readability.
  PAGE_NAME_MAX_LENGTH: 150,
  PAGE_GENERATION_MODEL: 'openai/gpt-4.1',
  PAGE_GENERATION_TEMP: '0.6',
  UPLOAD: {
    MAX_IMAGE_SIZE: 10 * 1024 * 1024, // 10MB
    MAX_VIDEO_SIZE: 50 * 1024 * 1024, // 50MB
  },
  MEDIA_LIBRARY: {
    PAGE_SIZE: 30,
  },
  IMAGE_PROMPT: {
    // The character counter stays hidden until the prompt gets close to the limit.
    COUNTER_VISIBLE_FROM: 1800,
  },
  PAGE_LAYOUT: {
    BLOCK_SPACING_PX: { small: 8, medium: 20, large: 40 },
    DEFAULT_BLOCK_SPACING_PX: 20,
    DEFAULT_WEBPAGE_BLOCK_SPACING_PX: 28,
    WEBPAGE_WIDTH_PX: { sm: 850, md: 1050, lg: 1200 },
  },
  HEADER_FOOTER: {
    IMAGE_DEFAULT_HEIGHT_PX: 32,
    IMAGE_MIN_SIZE_PX: 12,
    IMAGE_MAX_HEIGHT_PX: 120,
    IMAGE_MAX_WIDTH_PX: 280,
    BAND_DEFAULT_RESERVE_PX: 56,
    BAND_EXTRA_PADDING_PX: 16,
  },
  BUILDER_CANVAS: {
    GATE_OFFSCREEN_PAGE_CONTENT: true,
    // At 15, 10-14 page opens painted in ~2.2s p50 against ~0.6s for gated 15+ page documents
    // (PostHog, Sept 2026).
    GATE_MIN_PAGE_COUNT: 10,
    CONTENT_WINDOW_ROOT_MARGIN: '200% 0px',
    INITIAL_CONTENT_PAGE_COUNT: 3,
    SCROLL_RESTORE_EPSILON_PX: 1,
    TARGET_SCROLL_RETRY_DELAY_MS: 50,
    TARGET_SCROLL_MAX_ATTEMPTS: 20,
    TARGET_SCROLL_SETTLE_DELAY_MS: 100,
    TARGET_SCROLL_SETTLE_MAX_ATTEMPTS: 3,
    TARGET_SCROLL_SETTLE_TOLERANCE_PX: 2,
    SIDEBAR_SCROLL_RETRY_DELAY_MS: 60,
    SIDEBAR_SCROLL_MAX_ATTEMPTS: 10,
    PREFLIGHT_MEASUREMENT_BATCH_SIZE: 4,
    PREFLIGHT_MEASUREMENT_POLL_MS: 50,
    PREFLIGHT_MEASUREMENT_TIMEOUT_MS: 10_000,
  },
  PAGE_FOCUS: {
    EXEMPT_SELECTOR:
      '[role="dialog"],[data-radix-popper-content-wrapper],.popover-content,[data-radix-portal],.builder-right-sidebar,',
    CONTAINER_SELECTOR: '[data-page-container]',
    ENTRY_ANIMATION_MS: 1000,
  },
  PAGE_VISIBILITY: {
    THRESHOLDS: [0, 0.25, 0.5, 0.75, 1],
    MIN_CONTENDER_COVERAGE: 0.5,
    COVERAGE_EPSILON: 0.001,
  },
  CONTENT_ANIMATION: {
    // A scrolled block starts animating once its top edge is 10% above the bottom of the viewport
    ROOT_MARGIN: '0px 0px -10% 0px',
    STAGGER_MS: 70,
    MAX_STAGGER_STEPS: 6,
    // The loading splash waits 200ms, then fades over 900ms; content starts rising once it's half gone
    SPLASH_HOLD_MS: 500,
  },
  ANALYTICS: {
    MIN_SECTION_VIEW_SECONDS: 1,
    SECTION_VIEW_DEDUPE_MS: 2000,
    MS_PER_SECOND: 1000,
  },
  SLATE: {
    BLUR_SETTLE_MS: 100,
  },
  PAGE_OVERFLOW_FIX: {
    MAX_SPLIT_ITERATIONS: 20,
    SPLIT_RETRY_DELAY_MS: 80,
    // How long to wait after a split before re-reading pageBoundaryMetrics, which are
    // recomputed asynchronously from a DOM measurement effect.
    METRICS_SETTLE_DELAY_MS: 120,
  },
  TEXT_BLOCK: {
    TYPOGRAPHY_STYLES: [
      {
        id: 'title',
        format: 'heading-one',
        tag: 'H1',
        translationKey: 'toolbar_item_style_title',
        defaultFontSize: 30,
      },
      {
        id: 'subtitle',
        format: 'heading-two',
        tag: 'H2',
        translationKey: 'toolbar_item_style_subtitle',
        defaultFontSize: 24,
      },
      {
        id: 'heading',
        format: 'heading-three',
        tag: 'H3',
        translationKey: 'toolbar_item_style_heading',
        defaultFontSize: 20,
      },
      {
        id: 'subheading',
        format: 'heading-four',
        tag: 'H4',
        translationKey: 'toolbar_item_style_subheading',
        defaultFontSize: 18,
      },
      {
        id: 'body-text',
        format: 'paragraph',
        tag: 'T1',
        translationKey: 'toolbar_item_style_body_text',
        defaultFontSize: 16,
      },
      {
        id: 'small-text',
        format: 'paragraph',
        tag: 'T2',
        translationKey: 'toolbar_item_style_small_text',
        defaultFontSize: 14,
      },
      {
        id: 'caption',
        format: 'paragraph',
        tag: 'T3',
        translationKey: 'toolbar_item_style_caption',
        defaultFontSize: 12,
      },
      {
        id: 'pull-quote',
        format: 'block-quote',
        tag: null,
        translationKey: 'toolbar_item_style_pull_quote',
        defaultFontSize: 16,
      },
    ] as const,
    FONT_SIZE_PRESETS: [
      8, 10, 12, 14, 16, 18, 21, 24, 28, 32, 36, 40, 46, 48, 54, 60, 66, 72, 84, 96, 108, 120,
    ],
    // On phones, sizes up to the Heading 1 default keep their size; only larger ones scale down.
    MOBILE_FONT_SIZE_FLOOR: 30,
    FONT_SIZE_MIN: 8,
    FONT_SIZE_MAX: 120,
    FONT_SIZE_STEP: 1,
  },
  VIEW_HEADER: {
    TOP_THRESHOLD_PX: 40,
    SCROLL_DOWN_DELTA_PX: 10,
    SCROLL_UP_DELTA_PX: 8,
    SCROLLED_INDICATOR_THRESHOLD_PX: 10,
  },
  CROSS_BLOCK_SELECTION: {
    TOOLBAR_GAP_PX: 8,
    VIEWPORT_PADDING_PX: 12,
  },
  // Welcome modal shown after an AI client (ChatGPT, Claude) is authorized from the consent page.
  AI_CLIENT_CONNECTED_AUTO_CLOSE_SECONDS: 10,
};

export const Redirects = {
  DASHBOARD: {
    LOGIN: `${Config.DASHBOARD_URL}/login`,
    // Login that brings the user back to the current page once they sign in.
    LOGIN_RETURN_HERE: () =>
      `${Config.DASHBOARD_URL}/login?next=${encodeURIComponent(window.location.href)}`,
    SIGNUP: `${Config.DASHBOARD_URL}/signup`,
    SIGNUP_RETURN_HERE: () =>
      `${Config.DASHBOARD_URL}/signup?next=${encodeURIComponent(window.location.href)}`,
    // Org-scoped pages mirror apps/next's `routes.ts`: org is named in the URL, never left to
    // the session's last-active org. Callers pass the org the builder is actually working in.
    HOME: (org_id: number) => `${Config.DASHBOARD_URL}/${org_id}/dashboard`,
    TEAMS: (org_id: number) => `${Config.DASHBOARD_URL}/${org_id}/settings/team`,
    BILLING: (org_id: number, interval?: 'monthly' | 'annual') =>
      `${Config.DASHBOARD_URL}/${org_id}/settings/billing${interval ? `?interval=${interval}` : ''}`,
    EMAIL_SENDERS: (org_id: number) =>
      `${Config.DASHBOARD_URL}/${org_id}/settings/email-notifications/senders`,
    CONTACTS: (org_id: number) => `${Config.DASHBOARD_URL}/${org_id}/contacts`,
    START_WITH_TEMPLATE: (org_id: number) =>
      `${Config.DASHBOARD_URL}/${org_id}/start-with-template`,
    UPLOAD_DOCUMENT: (org_id: number) => `${Config.DASHBOARD_URL}/${org_id}/upload-document`,
    REPORT: (org_id: number, proposal_id: number | string) =>
      `${Config.DASHBOARD_URL}/${org_id}/reports/${proposal_id}`,
  },
  PROPOSAL_NOT_FOUND: '/proposal-not-found',
};
